import {
	memo,
	useCallback,
	useMemo,
	useRef,
} from "react";

import {
	Link,
	useNavigate,
} from "react-router-dom";

import "./collection.css";

const ArrowRightIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="24"
		height="24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M5 12h14M13 6l6 6-6 6" />
	</svg>
);

const getImageUrl = (image) => {
	if (!image) return "";

	return image.startsWith("http")
		? image
		: `https://admin.monsinidress.com/${image}`;
};

const ProductCard = memo(({ item }) => {
	const image = item?.images?.[0] || "";

	if (!image) return null;

	const imageUrl = getImageUrl(image);
	return (
		<Link
			to={`/product/id/${item.style}`}
			state={{ product: item }}
			className="relative group flex-shrink-0 w-[250px] sm:w-[300px] md:w-[350px] h-[400px] sm:h-[400px] md:h-[450px]"
			aria-label={`View dress ${item.style}`}
		>
			<img
				src={imageUrl}
				alt={`Monsini dress ${item.style || ""}`}
				loading="lazy"
				decoding="async"
				fetchPriority="high"
				width="350"
				height="450"
				className="w-full h-full object-cover rounded-xl shadow-md"
			/>

			<p className="flex justify-center items-center font-poppins absolute bottom-4 right-2 text-white text-sm md:text-base px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-500">
				{item.style}

				<span className="ms-5 hidden md:block">
					<ArrowRightIcon />
				</span>
			</p>
		</Link>
	);
});

ProductCard.displayName = "ProductCard";

function Collections({
	data = [],
	years = [],
}) {
	const navigate = useNavigate();

	const scrollRef1 = useRef(null);
	const scrollRef2 = useRef(null);
	const scrollRef3 = useRef(null);

	// ----------------------------------------------------
	// SORT YEARS
	// ----------------------------------------------------

	const sortedYears = useMemo(() => {
		if (!Array.isArray(years)) {
			return [];
		}

		return [...years].sort(
			(a, b) => b - a
		);
	}, [years]);

	// ----------------------------------------------------
	// PRODUCTS
	// ----------------------------------------------------

	const getProductsByCategory =
		useCallback(
			(
				categoryKeywords,
				maxProducts
			) => {
				if (
					!Array.isArray(data) ||
					data.length === 0
				) {
					return [];
				}

				const products = [];

				for (const year of sortedYears) {
					if (
						products.length >=
						maxProducts
					) {
						break;
					}

					const available =
						data.filter(
							(item) =>
								(item?.viewInfront === true || String(item?.viewInfront).toLowerCase() === "true" || item?.viewInfront === 1) &&
								String(
									item?.availability ||
										""
								).toLowerCase() ===
									"yes" &&
								item?.year ===
									year &&
								item?.category &&
								categoryKeywords.some(
									(
										keyword
									) =>
										String(
											item.category
										)
											.toLowerCase()
											.includes(
												keyword.toLowerCase()
											)
								)
						);

					for (const product of available) {
						if (
							products.length >=
								maxProducts
						) {
							break;
						}

						if (
							!products.some(
								(p) =>
									p._id ===
									product._id
							)
						) {
							products.push(
								product
							);
						}
					}

					if (
						products.length <
						maxProducts
					) {
						const otherVisible =
							data.filter(
								(item) =>
									(item?.viewInfront === true || String(item?.viewInfront).toLowerCase() === "true" || item?.viewInfront === 1) &&
									item?.year ===
										year &&
									item?.category &&
									categoryKeywords.some(
										(
											keyword
										) =>
											String(
												item.category
											)
												.toLowerCase()
												.includes(
													keyword.toLowerCase()
												)
									) &&
									!products.some(
										(p) =>
											p._id ===
											item._id
									)
							);

						for (const product of otherVisible) {
							if (
								products.length >=
									maxProducts
							) {
								break;
							}

							products.push(
								product
							);
						}
					}
				}

				return products;
			},
			[data, sortedYears]
		);

	const prom = useMemo(
		() =>
			getProductsByCategory(
				["Prom"],
				5
			),
		[getProductsByCategory]
	);

	const evening = useMemo(
		() =>
			getProductsByCategory(
				[
					"Enchanted Evening",
				],
				5
			),
		[getProductsByCategory]
	);

	const bridal = useMemo(
		() =>
			getProductsByCategory(
				[
					"Enchanted Bridal",
				],
				10
			),
		[getProductsByCategory]
	);

	// ----------------------------------------------------
	// SCROLL
	// ----------------------------------------------------

	const scrollLeft = useCallback(
		(ref) => {
			ref.current?.scrollBy({
				left: -360,
				behavior: "smooth",
			});
		},
		[]
	);

	const scrollRight = useCallback(
		(ref) => {
			ref.current?.scrollBy({
				left: 360,
				behavior: "smooth",
			});
		},
		[]
	);

	const highestYear =
		sortedYears.length > 0
			? sortedYears[0]
			: 2026;

	const getCollectionYearText =
		useCallback(
			(collection, baseText) => {
				if (
					!Array.isArray(
						collection
					) ||
					collection.length === 0
				) {
					return baseText;
				}

				const uniqueYears = [
					...new Set(
						collection
							.map(
								(item) =>
									item.year
							)
							.filter(
								Boolean
							)
					),
				].sort(
					(a, b) => b - a
				);

				if (
					uniqueYears.length === 0
				) {
					return baseText;
				}

				if (
					uniqueYears.length === 1
				) {
					return `${uniqueYears[0]} ${baseText}`;
				}

				return `${uniqueYears.join(
					" & "
				)} ${baseText}`;
			},
			[]
		);

	return (
		<section
			className="perf-defer-section mb-20 px-4 md:px-10"
			aria-labelledby="popular-collections-heading"
		>
			<div className="text-center font-lora font-semibold text-3xl md:text-5xl mb-20">
				<h2 id="popular-collections-heading">
					Our Popular Collections
				</h2>
			</div>

			{/* PROM */}

			{prom.length > 0 && (
				<div className="w-full flex flex-col md:flex-row items-start gap-10">
					<div className="md:w-1/3 w-full lg:min-h-[400px] h-auto flex justify-center items-center rounded-xl">
						<div className="space-y-6 h-fit">
							<h3 className="font-lora text-2xl md:text-3xl font-bold">
								{getCollectionYearText(
									prom,
									"Prom Gowns"
								)}
							</h3>

							<p className="font-poppins text-gray-600 text-base md:text-lg">
								Go to Prom in Style
								with our latest{" "}
								{getCollectionYearText(
									prom,
									"Prom Gowns"
								).toLowerCase()}{" "}
								featuring dazzling
								dress designs.
							</p>

							<button
								type="button"
								onClick={() =>
									navigate(
										`/prom/${highestYear}`
									)
								}
								className="px-6 py-3 font-lora bg-black text-white text-base md:text-lg font-semibold rounded-full hover:bg-gray-900 transition"
							>
								Explore
								Collection
							</button>
						</div>
					</div>

					<div className="md:w-2/3 w-full relative">
						<div
							ref={scrollRef1}
							className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar"
						>
							{prom.map(
								(
									item,
									index
								) => (
									<ProductCard
										key={
											item._id ||
											`${item.style}-${item.year}-${index}`
										}
										item={
											item
										}
									/>
								)
							)}
						</div>

						<button
							type="button"
							aria-label="Previous Prom dresses"
							onClick={() =>
								scrollLeft(
									scrollRef1
								)
							}
							className="absolute left-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex"
						>
							<span aria-hidden="true">
								&#8592;
							</span>
						</button>

						<button
							type="button"
							aria-label="Next Prom dresses"
							onClick={() =>
								scrollRight(
									scrollRef1
								)
							}
							className="absolute right-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex"
						>
							<span aria-hidden="true">
								&#8594;
							</span>
						</button>
					</div>
				</div>
			)}

			{/* EVENING */}

			{evening.length > 0 && (
				<div className="w-full flex flex-col md:flex-row items-start gap-10 mt-20">
					<div className="md:w-2/3 w-full relative order-2 md:order-1">
						<div
							ref={scrollRef2}
							className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar"
						>
							{evening.map(
								(
									item,
									index
								) => (
									<ProductCard
										key={
											item._id ||
											`${item.style}-${item.year}-${index}`
										}
										item={
											item
										}
									/>
								)
							)}
						</div>

						<button
							type="button"
							aria-label="Previous Evening dresses"
							onClick={() =>
								scrollLeft(
									scrollRef2
								)
							}
							className="absolute left-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex"
						>
							<span aria-hidden="true">
								&#8592;
							</span>
						</button>

						<button
							type="button"
							aria-label="Next Evening dresses"
							onClick={() =>
								scrollRight(
									scrollRef2
								)
							}
							className="absolute right-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex"
						>
							<span aria-hidden="true">
								&#8594;
							</span>
						</button>
					</div>

					<div className="md:w-1/3 w-full lg:min-h-[400px] h-auto flex justify-center items-center rounded-xl order-1 md:order-2">
						<div className="space-y-6 h-fit">
							<h3 className="font-lora text-2xl md:text-3xl font-bold">
								{getCollectionYearText(
									evening,
									"Enchanted Evening Gowns"
								)}
							</h3>

							<p className="font-poppins text-gray-600 text-base md:text-lg">
								Step into elegance
								with our beautiful
								evening dress
								collection.
							</p>

							<button
								type="button"
								onClick={() =>
									navigate(
										`/evening/${highestYear}`
									)
								}
								className="px-6 py-3 font-lora bg-black text-white text-base md:text-lg font-semibold rounded-full hover:bg-gray-900 transition"
							>
								Explore
								Collection
							</button>
						</div>
					</div>
				</div>
			)}

			{/* BRIDAL */}

			{bridal.length > 0 && (
				<div className="w-full flex flex-col items-start gap-10 mt-20">
					<div className="w-full h-auto flex justify-center items-center rounded-xl">
						<div className="space-y-6 h-fit text-center">
							<h3 className="font-lora text-2xl md:text-3xl font-bold">
								{getCollectionYearText(
									bridal,
									"Enchanted Bridal Gowns"
								)}
							</h3>

							<p className="font-poppins text-gray-600 text-base md:text-lg">
								Find the perfect
								wedding gown that
								makes your special
								day unforgettable.
							</p>

							<button
								type="button"
								onClick={() =>
									navigate(
										`/bridal/${highestYear}`
									)
								}
								className="px-6 py-3 font-lora bg-black text-white text-base md:text-lg font-semibold rounded-full hover:bg-gray-900 transition"
							>
								Explore
								Collection
							</button>
						</div>
					</div>

					<div className="w-full relative">
						<div
							ref={scrollRef3}
							className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar"
						>
							{bridal.map(
								(
									item,
									index
								) => (
									<ProductCard
										key={
											item._id ||
											`${item.style}-${item.year}-${index}`
										}
										item={
											item
										}
									/>
								)
							)}
						</div>

						<button
							type="button"
							aria-label="Previous Bridal dresses"
							onClick={() =>
								scrollLeft(
									scrollRef3
								)
							}
							className="absolute left-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex"
						>
							<span aria-hidden="true">
								&#8592;
							</span>
						</button>

						<button
							type="button"
							aria-label="Next Bridal dresses"
							onClick={() =>
								scrollRight(
									scrollRef3
								)
							}
							className="absolute right-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex"
						>
							<span aria-hidden="true">
								&#8594;
							</span>
						</button>
					</div>
				</div>
			)}
		</section>
	);
}

export default memo(Collections);